import { Navigate, Route, Routes } from 'react-router-dom'
import { ResetPasswordPage } from '@modules/reset-password/reset-password.page.component'
import { ForgotPasswordPage } from '@modules/forgot-password/forgot-password.page.component'
import { LoginPage } from '@modules/login/login.page.component'
import ProtectedRoute from '@shared/components/protected-route.component'
import { CompanyProfilePage } from '@modules/company-profile/index.page'
import { HistoryPage } from '@modules/history/history.page.component'
import { RequirementPage } from '@modules/requirement/requirement.page.component'
import { SelectLocationClientPage } from '@modules/select-location/select-location-client.page.component'
import { SelectLocationPage } from '@modules/select-location/select-location.page.component'
import { SelectScopesPage } from '@modules/select-scopes/select-scopes.page.component'
import { MetricsPage } from '@modules/metrics/metrics.page.component'
import { AdminRequirementsCrudPage } from '@modules/admin-requirements/admin-requirements-crud.page.component'
import {
  AdminGenericCrudPage,
  AdminGenericCrudPageChild,
} from '@modules/admin-generic/admin-generic-crud.page.component'
import React from 'react'
import { useNavigatorOnline } from '@oieduardorabelo/use-navigator-online'
import { ClientAdminResourceType, Roles } from '@shared/types'
import { useACL } from '@shared/hooks/use-acl.hook'
import { useRouter } from '@shared/hooks/use-router'
import { AdminRequirementCrudDialog } from '@modules/admin-requirements/admin-requirement-crud.dialog.component'
import { AdminGenericCrudDialog } from '@modules/admin-generic/admin-generic-crud.dialog.component'
import { AspectsPage } from '@modules/aspects/aspects.page.component'
import { AspectsDialog } from '@modules/aspects/aspects.dialog.component'
import { DecisionsPage } from '@modules/decisions/decisions.page.component'
import { DecisionsDialog } from '@modules/decisions/decisions.dialog.component'
import { DocumentsDialog } from '@modules/documents/documents.dialog.component'
import { FilesDialog } from '@modules/files/files.dialog.component'
import { FilesPage } from '@modules/files/files.page.component'
import { DocumentsPage } from '@modules/documents/documents.page.component'
import { ActsPage } from '@modules/requirements/acts.page.component'
import { ScopesCrudDialog } from '@modules/scopes/scopes-crud.dialog.component'
import { NewslettersPage } from '@modules/requirements/newsletters.page.component'
import { RequirementAssessmentPage } from '@modules/company-profile/requirement-assessment/index.page'
import { ManageScopesPage } from '@modules/requirements/manage-scopes.page.component'

export function ApplicationRoutes() {
  const { location } = useRouter()
  const { is: isAllowed } = useACL()
  const { isOffline } = useNavigatorOnline()
  const lastActiveAudit: { companyID: number; auditID: number } = JSON.parse(
    localStorage.getItem('last-active-audit') ?? 'null'
  )
  const state = location.state as { backgroundLocation?: Location }
  const hasRole = isAllowed([
    Roles.Admin,
    Roles.Auditor,
    Roles.Client,
    Roles.ClientEditor,
    Roles.ClientBasic,
    Roles.ClientAdministrator,
  ])
  const isClient = isAllowed([
    Roles.Client,
    Roles.ClientEditor,
    Roles.ClientBasic,
    Roles.ClientAdministrator,
  ])
  const isModifyDecisionAllowed = isAllowed([
    Roles.Admin,
    Roles.Auditor,
    Roles.ClientAdministrator,
    Roles.ClientEditor,
  ])
  const isAuditor = isAllowed([Roles.Admin, Roles.Auditor])
  const isClientAdmin = isAllowed([Roles.ClientAdministrator])

  return (
    <>
      <Routes location={state?.backgroundLocation || location}>
        <Route path="/resetpassword" element={<ResetPasswordPage />} />
        <Route path="/forgotpassword" element={<ForgotPasswordPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/company-profile/:companyID"
          element={
            <ProtectedRoute
              isAllowed={hasRole}
              component={<CompanyProfilePage isPreviewMode={true} />}
            />
          }
        />
        <Route
          path="/company-profile/:companyID/:auditID?/run"
          element={
            <ProtectedRoute
              isAllowed={hasRole}
              component={<ManageScopesPage />}
            />
          }
        />
        <Route
          path="/company-profile/:companyID/:auditID"
          element={
            <ProtectedRoute
              isAllowed={isAllowed([Roles.Admin, Roles.Auditor])}
              component={<CompanyProfilePage isPreviewMode={false} />}
            />
          }
        />

        <Route
          path="/company-profile/:companyID/config"
          element={
            <ProtectedRoute
              isAllowed={hasRole}
              component={<SelectScopesPage />}
            />
          }
        />
        <Route
          path="/company-profile/:companyID/requirement/:requirementID"
          element={
            <ProtectedRoute
              isAllowed={hasRole}
              component={<RequirementAssessmentPage type="published" />}
            />
          }
        />
        <Route
          path="/company-profile/:companyID/esqula-requirement/:requirementID"
          element={
            <ProtectedRoute
              isAllowed={hasRole}
              component={<RequirementAssessmentPage type="esqula" />}
            />
          }
        />
        <Route
          path="/company-profile/:companyID/esqula-requirement/:requirementID/edit"
          element={
            <ProtectedRoute
              isAllowed={hasRole}
              component={<RequirementAssessmentPage type="esqula" />}
            />
          }
        />
        <Route
          path="/company-profile/:companyID/requirement/:requirementID/edit"
          element={
            <ProtectedRoute
              isAllowed={hasRole}
              component={<RequirementAssessmentPage type="published" />}
            />
          }
        />
        <Route
          path="/company-profile/:companyID/:auditID/requirement/:requirementID/edit"
          element={
            <ProtectedRoute
              isAllowed={hasRole}
              component={<RequirementAssessmentPage type="assessment" />}
            />
          }
        />
        <Route
          path="/company-profile/:companyID/:auditID?/acts/:scopeID"
          element={
            <ProtectedRoute isAllowed={hasRole} component={<ActsPage />} />
          }
        />
        <Route
          path="/audit/:companyID/newsletters"
          element={
            <ProtectedRoute
              isAllowed={hasRole}
              component={<NewslettersPage />}
            />
          }
        />
        <Route
          path="/history/:companyID"
          element={
            <ProtectedRoute isAllowed={hasRole} component={<HistoryPage />} />
          }
        />
        <Route
          path="/aspects/:companyID"
          element={
            <ProtectedRoute isAllowed={hasRole} component={<AspectsPage />} />
          }
        />
        <Route
          path={`/aspects/:companyID/edit/:resourceID`}
          element={
            <ProtectedRoute
              isAllowed={isModifyDecisionAllowed}
              component={<AspectsDialog />}
            />
          }
        />
        <Route
          path="/decisions/:companyID"
          element={
            <ProtectedRoute isAllowed={hasRole} component={<DecisionsPage />} />
          }
        />
        <Route
          path={`/decisions/:companyID/edit/:resourceID`}
          element={
            <ProtectedRoute
              isAllowed={isModifyDecisionAllowed}
              component={<DecisionsDialog />}
            />
          }
        />
        <Route
          path="/documents/:companyID"
          element={
            <ProtectedRoute isAllowed={hasRole} component={<DocumentsPage />} />
          }
        />
        <Route
          path={`/documents/:companyID/edit/:resourceID`}
          element={
            <ProtectedRoute
              isAllowed={isModifyDecisionAllowed}
              component={<DocumentsDialog />}
            />
          }
        />
        <Route
          path="/files"
          element={
            <ProtectedRoute isAllowed={hasRole} component={<FilesPage />} />
          }
        />
        <Route
          path={`/files/edit/:resourceID`}
          element={
            <ProtectedRoute
              isAllowed={isModifyDecisionAllowed}
              component={<FilesDialog />}
            />
          }
        />
        <Route
          path="/audit/:companyID/:auditID/:requirementID"
          element={
            <ProtectedRoute
              isAllowed={hasRole}
              component={<RequirementPage />}
            />
          }
        />
        <Route
          path="/select-location/client"
          element={
            <ProtectedRoute
              isAllowed={isClient}
              component={<SelectLocationClientPage />}
            />
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute
              isAllowed={hasRole}
              component={<Navigate to="/client" />}
            />
          }
        />
        <Route
          path="/client"
          element={
            <ProtectedRoute
              isAllowed={hasRole}
              component={<SelectLocationPage />}
            />
          }
        />
        <Route
          path="/client/:companyID/:auditID?"
          element={
            <ProtectedRoute
              isAllowed={hasRole}
              component={<SelectScopesPage />}
            />
          }
        />
        <Route
          path="/audit-report/:companyID/:auditID"
          element={
            <ProtectedRoute isAllowed={isAuditor} component={<MetricsPage />} />
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute
              isAllowed={isAuditor}
              component={<Navigate to="/admin/requirements" />}
            />
          }
        />
        <Route
          path="/admin/requirements"
          element={
            <ProtectedRoute
              isAllowed={isAuditor}
              component={<AdminRequirementsCrudPage />}
            />
          }
        />
        <Route
          path={'/admin/requirements/edit/:resourceID'}
          element={
            <ProtectedRoute
              isAllowed={isAuditor}
              component={<AdminRequirementCrudDialog />}
            />
          }
        />
        <Route
          path={'/admin/requirements/add'}
          element={
            <ProtectedRoute
              isAllowed={isAuditor}
              component={<AdminRequirementCrudDialog />}
            />
          }
        />
        <Route
          path={'/admin/scopes/edit/:resourceID'}
          element={
            <ProtectedRoute
              isAllowed={isAuditor}
              component={<ScopesCrudDialog />}
            />
          }
        />
        <Route
          path={'/admin/scopes/add'}
          element={
            <ProtectedRoute
              isAllowed={isAuditor}
              component={<ScopesCrudDialog />}
            />
          }
        />
        <Route
          path={`/admin/:resourceName`}
          element={
            <ProtectedRoute
              isAllowed={isAuditor}
              component={<AdminGenericCrudPage />}
            />
          }
        />
        <Route
          path={`/admin/:resourceName/add`}
          element={<AdminGenericCrudDialog />}
        />
        <Route
          path={`/admin/:resourceName/edit/:resourceID`}
          element={<AdminGenericCrudDialog />}
        />
        <Route
          path="/client-admin"
          element={
            <ProtectedRoute
              isAllowed={isClientAdmin}
              component={<Navigate to="/client-admin/users" />}
            />
          }
        />
        <Route
          path={`/client-admin/users/add`}
          element={
            <AdminGenericCrudDialog
              resourceNameOverride={ClientAdminResourceType.USERS}
            />
          }
        />
        <Route
          path={`/client-admin/users/edit/:resourceID`}
          element={
            <AdminGenericCrudDialog
              resourceNameOverride={ClientAdminResourceType.USERS}
            />
          }
        />
        <Route
          path={`/client-admin/users`}
          element={
            <ProtectedRoute
              isAllowed={isClientAdmin}
              component={
                <AdminGenericCrudPageChild
                  resourceNameOverride={ClientAdminResourceType.USERS}
                />
              }
            />
          }
        />
        {isOffline && lastActiveAudit ? (
          <Navigate
            to={`/audit/${lastActiveAudit.companyID}/${lastActiveAudit.auditID}`}
          />
        ) : null}
      </Routes>
      {state?.backgroundLocation && (
        <Routes>
          <Route
            path={'/admin/requirements/edit/:resourceID'}
            element={
              <ProtectedRoute
                isAllowed={isAuditor}
                component={<AdminRequirementCrudDialog />}
              />
            }
          />
          <Route
            path={'/admin/requirements/add'}
            element={
              <ProtectedRoute
                isAllowed={isAuditor}
                component={<AdminRequirementCrudDialog />}
              />
            }
          />
          <Route
            path={'/admin/scopes/edit/:resourceID'}
            element={
              <ProtectedRoute
                isAllowed={isAuditor}
                component={<ScopesCrudDialog />}
              />
            }
          />
          <Route
            path={'/admin/scopes/add'}
            element={
              <ProtectedRoute
                isAllowed={isAuditor}
                component={<ScopesCrudDialog />}
              />
            }
          />
          <Route
            path={`/admin/:resourceName/add`}
            element={<AdminGenericCrudDialog />}
          />
          <Route
            path={`/admin/:resourceName/edit/:resourceID`}
            element={<AdminGenericCrudDialog />}
          />
          <Route
            path={`/client-admin/users/add`}
            element={
              <AdminGenericCrudDialog
                resourceNameOverride={ClientAdminResourceType.USERS}
              />
            }
          />
          <Route
            path={`/client-admin/users/edit/:resourceID`}
            element={
              <AdminGenericCrudDialog
                resourceNameOverride={ClientAdminResourceType.USERS}
              />
            }
          />
          <Route
            path={`/decisions/:companyID/edit/:resourceID`}
            element={
              <ProtectedRoute
                isAllowed={isModifyDecisionAllowed}
                component={<DecisionsDialog />}
              />
            }
          />
          <Route
            path={`/documents/:companyID/edit/:resourceID`}
            element={
              <ProtectedRoute
                isAllowed={isModifyDecisionAllowed}
                component={<DocumentsDialog />}
              />
            }
          />
          <Route
            path={`/files/edit/:resourceID`}
            element={
              <ProtectedRoute
                isAllowed={isModifyDecisionAllowed}
                component={<FilesDialog />}
              />
            }
          />
          <Route
            path={`/aspects/:companyID/edit/:resourceID`}
            element={
              <ProtectedRoute
                isAllowed={isModifyDecisionAllowed}
                component={<AspectsDialog />}
              />
            }
          />
        </Routes>
      )}
    </>
  )
}
